// taobaoService.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Search for products on Taobao
 * @param {string} query - Search query (already translated to Chinese)
 * @param {Object} options - Additional search options
 * @param {number} [options.page] - Page number
 * @param {number} [options.pageSize] - Number of items per page
 * @param {string} [options.sort] - Sort order (e.g., 'PRICE_ASC', 'PRICE_DESC')
 * @param {number} [options.min_price] - Minimum price for filtering (in Fen)
 * @param {number} [options.max_price] - Maximum price for filtering (in Fen)
 * @returns {Promise<Object>} - Search results
 */
export const searchTaobaoProducts = async (query, options = {}) => {
  try {
    const pageSize = Math.min(Number(options.pageSize) || 10, 20);
    
    const searchUrl = API_BASE_URL.endsWith('/api') 
      ? `${API_BASE_URL}/taobao/search`
      : `${API_BASE_URL}/api/taobao/search`;
      
    const requestParams = {
      q: query,
      page: Number(options.page) || 1,
      pageSize: pageSize,
      sort: options.sort || 'default', // Keep 'default' for internal mapping if needed
    };

    // --- ADDITION START ---
    const filters = [];
    if (options.min_price !== undefined && options.min_price !== null) { // Check for undefined/null instead of just truthiness (0 should be valid)
      filters.push(`min_price:${options.min_price}`);
    }
    if (options.max_price !== undefined && options.max_price !== null) { // Check for undefined/null instead of just truthiness
      filters.push(`max_price:${options.max_price}`);
    }
    if (filters.length > 0) {
      requestParams.filters = filters.join(',');
    }
    // --- ADDITION END ---
    
    console.log('Making Taobao search request:', {
      url: searchUrl,
      params: requestParams
    });

    const response = await axios.get(searchUrl, {
      params: requestParams,
      withCredentials: true,
    });
    
    console.log('Received response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data
    });
    
    console.log('Raw API response:', response.data);
    
    if (response.data.success && Array.isArray(response.data.data)) {
      const items = response.data.data;
      const pagination = response.data.pagination || {};
      
      console.log('Processed items:', items);
      console.log('Pagination data:', pagination);
      
      return {
        items: items.map(item => ({
          ...item,
          id: item.id || item.itemId,
          title: item.multiLanguageInfo?.title || item.title,
          price: item.price,
          original_price: item.original_price,
          pic_url: item.pic_url || item.mainImageUrl,
          detail_url: item.detail_url || `https://item.taobao.com/item.htm?id=${item.id || item.itemId}`,
          shop: item.shop || { nick: item.shopName },
          sales: item.sales || 0,
          multiLanguageInfo: item.multiLanguageInfo
        })),
        total: pagination.total || items.length,
        page: parseInt(pagination.page) || 1,
        pageSize: parseInt(pagination.pageSize) || 10,
        success: true
      };
    }
    
    console.error('Unexpected response format:', response.data);
    throw new Error('Unexpected response format from server');
  } catch (error) {
    console.error('Error searching Taobao products:', {
      message: error.message,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        headers: error.response.headers
      } : 'No response',
      request: error.request ? 'Request made but no response' : 'No request',
      config: error.config ? {
        url: error.config.url,
        method: error.config.method,
        params: error.config.params
      } : 'No config'
    });
    
    if (error.response) {
      throw new Error(error.response.data?.error || error.response.data?.details || `Server error: ${error.response.status} ${error.response.statusText}`);
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to search Taobao products');
    }
  }
};

export const getTaobaoProductDetails = async (productId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/taobao/product/${productId}`, {
      withCredentials: true,
    });
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Product not found');
    }
    
    return response.data.product;
  } catch (error) {
    console.error('Error fetching Taobao product details:', error);
    
    if (error.response) {
      if (error.response.status === 404) {
        throw new Error('Product not found');
      }
      throw new Error(error.response.data?.error || error.response.data?.details || 'Failed to fetch product details');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch product details');
    }
  }
};

export const importTaobaoProduct = async (url, productData = {}) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/taobao/import`, {
      url,
      productData,
    }, {
      withCredentials: true,
    });
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to import product');
    }
    
    return response.data.product;
  } catch (error) {
    console.error('Error importing Taobao product:', error);
    
    if (error.response) {
      throw new Error(error.response.data?.error || error.response.data?.details || 'Failed to import product');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to import product');
    }
  }
};

export const extractProductIdFromUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const id = urlObj.searchParams.get('id');
    return id;
  } catch (error) {
    console.error('Error extracting product ID from URL:', error);
    return null;
  }
};

/**
 * Validate if a URL is a valid Taobao/Tmall product URL
 * @param {string} url - URL to validate
 * @returns {boolean} - Whether the URL is valid
 */
export const isValidTaobaoUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const validHosts = [
      'detail.taobao.com',
      'detail.tmall.com',
      'item.taobao.com',
      'item.tmall.com'
    ];
    
    return validHosts.includes(urlObj.hostname) && urlObj.searchParams.has('id');
  } catch (error) {
    return false;
  }
};

/**
 * Test backend connectivity
 * @returns {Promise<Object>} - Test results
 */
export const testBackendConnection = async () => {
  try {
    console.log('Testing backend connection...');
    console.log('API_BASE_URL:', API_BASE_URL);
    
    // First test basic server health
    try {
      const healthResponse = await axios.get(`${API_BASE_URL.replace('/api', '')}/health`, {
        withCredentials: true,
        timeout: 5000
      });
      console.log('Health check response:', healthResponse.data);
    } catch (healthError) {
      console.warn('Health check failed:', healthError.message);
    }
    
    // Then test Taobao route
    const response = await axios.get(`${API_BASE_URL}/taobao/test`, {
      withCredentials: true,
      timeout: 10000
    });
    
    console.log('Taobao test response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Backend test failed:', {
      message: error.message,
      code: error.code,
      response: error.response ? {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: error.response.config?.url
      } : 'No response',
      config: {
        url: error.config?.url,
        method: error.config?.method
      }
    });
    throw new Error(error.response?.data?.error || error.message || 'Backend connection test failed');
  }
};

/**
 * Debug function to check all route availability
 * @returns {Promise<Object>} - Debug results
 */
export const debugRoutes = async () => {
  const baseUrl = API_BASE_URL.replace('/api', '');
  const routes = [
    `${baseUrl}/health`,
    `${API_BASE_URL}/taobao/test`,
    `${API_BASE_URL}/taobao/search?q=test&page=1&pageSize=5`
  ];
  
  const results = [];
  
  for (const route of routes) {
    try {
      console.log(`Testing route: ${route}`);
      const response = await axios.get(route, {
        withCredentials: true,
        timeout: 5000,
        validateStatus: () => true // Don't throw on any status
      });
      
      results.push({
        route,
        status: response.status,
        statusText: response.statusText,
        success: response.status < 400,
        data: response.data
      });
    } catch (error) {
      results.push({
        route,
        status: error.response?.status || 'No response',
        statusText: error.response?.statusText || 'Network error',
        success: false,
        error: error.message
      });
    }
  }
  
  console.log('Route debug results:', results);
  return results;
};